import { reportingPrisma as prisma } from "client";

export type EventTicketOid = { $oid: string };

export type EventTicketWithPrice = EventTicketOid & { price: number };

/** Ticket ObjectIds for an event — use to early-$match OrderItem pipelines. */
export const getEventTicketOids = async (
  eventId: string
): Promise<EventTicketOid[]> => {
  const tickets = await prisma.ticket.findMany({
    where: {
      eventId,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });
  return tickets.map((ticket) => ({ $oid: ticket.id }));
};

/**
 * Ticket types with bookable price — one query for quantity classification
 * (avoids per-row Ticket/BookableItem $lookups in aggregations).
 */
export const getEventTicketsWithPrices = async (
  eventId: string
): Promise<EventTicketWithPrice[]> => {
  const tickets = await prisma.ticket.findMany({
    where: {
      eventId,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: {
      id: true,
      bookableItem: { select: { price: true } },
    },
  });
  return tickets.map((ticket) => ({
    $oid: ticket.id,
    price: ticket.bookableItem?.price ?? 0,
  }));
};

/** Resolve prices for known ticket oids (already filtered non-deleted). */
export const getTicketPricesByOids = async (
  ticketOids: EventTicketOid[]
): Promise<EventTicketWithPrice[]> => {
  if (ticketOids.length === 0) return [];
  const tickets = await prisma.ticket.findMany({
    where: { id: { in: ticketOids.map((oid) => oid.$oid) } },
    select: {
      id: true,
      bookableItem: { select: { price: true } },
    },
  });
  return tickets.map((ticket) => ({
    $oid: ticket.id,
    price: ticket.bookableItem?.price ?? 0,
  }));
};
